import React, { FC, useMemo } from 'react';
import { Crucible, RewardProgram } from 'types';
import { Box } from '@chakra-ui/layout';
import { useRewardPrograms } from 'store/zustand/useRewardPrograms';
import {
  Heading,
  Flex,
  Spinner,
  Text,
  useMediaQuery,
  Stack
} from '@chakra-ui/react';
import { Table, Thead, Tbody, Tr, Th, Td } from 'components/table';
import LockedAssetsAction from '../crucible/detail/summary/LockedAssetsAction';
import formatNumber from 'utils/formatNumber';
import TokenIcon from 'components/shared/TokenIcon';
import { useCrucibleAssets } from 'hooks/query/useCrucibleAssets';
import { useCrucible, useCrucibleLocks } from 'hooks/query/useCrucible';
import UnlockedAssetsAction from 'components/crucible/detail/summary/UnlockedAssetsAction';

type Props = {
  crucible: Crucible;
  setTabIndex: React.Dispatch<React.SetStateAction<number>>;
  setTransferTabIndex: React.Dispatch<React.SetStateAction<number>>;
};

const ReadonlySummary: FC<Props> = ({
  crucible,
  setTabIndex,
  setTransferTabIndex
}) => {
  const { isIdle, isLoading } = useCrucible();
  const { status: lockStatus, data: crucibleLocks } = useCrucibleLocks();
  const { rewardPrograms } = useRewardPrograms();
  const [isLargerThan768] = useMediaQuery('(min-width: 769px)');

  const { data: crucibleAssets, status: assetsStatus } = useCrucibleAssets(
    crucible?.id
  );

  const selectedCrucibleLocks = useMemo(() => {
    if (crucible?.id && crucibleLocks) {
      return crucibleLocks;
    }
    return [];
  }, [crucible, crucibleLocks]);

  if (
    lockStatus === 'idle' ||
    lockStatus === 'loading' ||
    assetsStatus === 'idle' ||
    assetsStatus === 'loading' ||
    isLoading ||
    isIdle
  ) {
    return (
      <Box p={[4, 8]} borderRadius='xl'>
        <Spinner />
      </Box>
    );
  }

  if (!crucible || !crucible.id || !crucibleAssets) {
    return (
      <Box p={[4, 8]} borderRadius='xl'>
        Something went wrong, please try refreshing the page.
      </Box>
    );
  }

  return (
    <Flex flexDir='column'>
      <Heading size='md' fontWeight='500' textAlign='left'>
        Locked assets
      </Heading>
      <Box
        mt={6}
        mb={10}
        borderWidth={1}
        borderRadius='xl'
        borderColor='gray.700'
        overflow='hidden'
      >
        {lockStatus === 'error' ? (
          <Text textAlign='left' color='gray.300' p={4}>
            Failed to get locked assets
          </Text>
        ) : selectedCrucibleLocks.length === 0 ? (
          <Text textAlign='left' color='gray.300' p={4}>
            No locked assets
          </Text>
        ) : (
          <Table size='md'>
            <Thead bg='gray.700'>
              <Tr>
                <Th>Asset in Crucible</Th>
                <Th>Amount</Th>
                <Th>Subscribed to</Th>
                <Th textAlign='right'>Menu</Th>
              </Tr>
            </Thead>
            <Tbody>
              {selectedCrucibleLocks?.map((lock, idx) => {
                const rewardProgram = rewardPrograms.find(
                  (p) =>
                    p.address.toLowerCase() ===
                    lock.rewardProgramAddress.toLowerCase()
                );
                return (
                  <Tr key={idx}>
                    <Td
                      bg={isLargerThan768 ? 'none' : 'gray.700'}
                      width={isLargerThan768 ? '240px' : 'none'}
                    >
                      <Stack direction='row' alignItems='center'>
                        <TokenIcon asset={lock.subscribedToken} />
                        <Text maxW='184px' isTruncated>
                          {lock.subscribedToken.symbol}
                        </Text>
                      </Stack>
                    </Td>
                    <Td>
                      {formatNumber.token(
                        lock.subscribedToken.value.amount,
                        lock.subscribedToken.decimals
                      )}
                    </Td>
                    <Td>{rewardProgram?.name}</Td>
                    <Td
                      width={isLargerThan768 ? '160px' : 'none'}
                      textAlign='right'
                    >
                      <LockedAssetsAction
                        isReadOnly={true}
                        lock={lock}
                        rewardProgram={rewardProgram as RewardProgram}
                        crucibleId={crucible.id}
                        setTabIndex={setTabIndex}
                      />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Box>
      <Heading size='md' fontWeight='500' textAlign='left'>
        Unlocked assets
      </Heading>
      <Box
        mt={6}
        borderWidth={1}
        borderRadius='xl'
        borderColor='gray.700'
        overflow='hidden'
      >
        {assetsStatus === 'error' ? (
          <Text textAlign='left' color='gray.300' p={4}>
            Failed to get unlocked assets
          </Text>
        ) : crucibleAssets.length === 0 ? (
          <Text textAlign='left' color='gray.300' p={4}>
            No unlocked assets
          </Text>
        ) : (
          <Table size='md'>
            <Thead bg='gray.700'>
              <Tr>
                <Th width={['auto', 'auto', '240px']}>Asset in Crucible</Th>
                <Th>Amount</Th>
                <Th width={['auto', 'auto', '160px']} textAlign='right'>
                  Menu
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {crucibleAssets.map((asset) => (
                <Tr key={asset.address}>
                  <Td bg={['gray.700', null, 'none']}>
                    <Stack direction='row' alignItems='center'>
                      <TokenIcon asset={asset} />
                      <Text maxW='184px' isTruncated>
                        {asset.symbol}
                      </Text>
                    </Stack>
                  </Td>
                  <Td>
                    {formatNumber.token(asset.value.amount, asset.decimals)}
                  </Td>
                  <Td
                    width={isLargerThan768 ? '160px' : 'none'}
                    textAlign='right'
                  >
                    <UnlockedAssetsAction
                      disabled
                      crucibleAddress={crucible.id}
                      asset={asset}
                      setTabIndex={setTabIndex}
                      setTransferTabIndex={setTransferTabIndex}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </Flex>
  );
};

export default ReadonlySummary;
